// Shared selection-key helpers for a proposal's ops/changes. Split out of
// ProposalCard so ProposalPreview can compute the same "what's selected"
// answer without duplicating the key scheme.

import {
  proposalOpDependencies,
  type AiApplyProposalRequest,
  type AiProposalOp,
} from "@checklist/shared";

export type SelectionKey = `op:${number}` | `change:${number}:${number}`;

export const atomicKey = (opIndex: number): SelectionKey => `op:${opIndex}`;
export const changeKey = (opIndex: number, changeIndex: number): SelectionKey =>
  `change:${opIndex}:${changeIndex}`;

/**
 * Tick or untick one choice while keeping "$ref" dependencies consistent: ticking
 * a template or checklist also ticks the new list/template it builds on, and
 * unticking one unticks everything built on it. The server rejects a selection
 * that breaks this (PROPOSAL_DEPENDENCY_UNSELECTED) rather than half-applying it.
 */
export function toggleChoice(
  ops: AiProposalOp[],
  selected: Set<SelectionKey>,
  key: SelectionKey,
  checked: boolean
): Set<SelectionKey> {
  const next = new Set(selected);
  if (checked) next.add(key);
  else next.delete(key);
  const match = /^op:(\d+)$/.exec(key);
  if (!match) return next;

  const stack = [Number(match[1])];
  while (stack.length) {
    const index = stack.pop()!;
    if (checked) {
      const op = ops[index];
      if (!op) continue;
      for (const dep of proposalOpDependencies(op)) {
        if (ops[dep] && !next.has(atomicKey(dep))) {
          next.add(atomicKey(dep));
          stack.push(dep);
        }
      }
    } else {
      ops.forEach((op, dependent) => {
        if (proposalOpDependencies(op).includes(index) && next.has(atomicKey(dependent))) {
          next.delete(atomicKey(dependent));
          stack.push(dependent);
        }
      });
    }
  }
  return next;
}

export function initialSelection(ops: AiProposalOp[]): Set<SelectionKey> {
  const selected = new Set<SelectionKey>();
  ops.forEach((op, opIndex) => {
    if (op.op === "items_change") {
      op.changes.forEach((change, changeIndex) => {
        if (change.op === "add") selected.add(changeKey(opIndex, changeIndex));
      });
    } else if (op.op === "new_list" || op.op === "new_template" || op.op === "new_checklist") {
      selected.add(atomicKey(opIndex));
    }
  });
  return selected;
}

export function allSelection(ops: AiProposalOp[]): Set<SelectionKey> {
  const selected = new Set<SelectionKey>();
  ops.forEach((op, opIndex) => {
    if (op.op === "items_change") {
      op.changes.forEach((_, changeIndex) => selected.add(changeKey(opIndex, changeIndex)));
    } else {
      selected.add(atomicKey(opIndex));
    }
  });
  return selected;
}

export function selectionCount(ops: AiProposalOp[], selected: Set<SelectionKey>): number {
  return ops.reduce((count, op, opIndex) => {
    if (op.op === "items_change") {
      return (
        count +
        op.changes.filter((_, changeIndex) => selected.has(changeKey(opIndex, changeIndex))).length
      );
    }
    return count + Number(selected.has(atomicKey(opIndex)));
  }, 0);
}

export function totalChoices(ops: AiProposalOp[]): number {
  return ops.reduce((count, op) => count + (op.op === "items_change" ? op.changes.length : 1), 0);
}

export function makeRequest(ops: AiProposalOp[], selected: Set<SelectionKey>): AiApplyProposalRequest {
  return {
    selection: ops.flatMap((op, opIndex) => {
      if (op.op !== "items_change") {
        return selected.has(atomicKey(opIndex)) ? [{ opIndex }] : [];
      }
      const changeIndexes = op.changes.flatMap((_, changeIndex) =>
        selected.has(changeKey(opIndex, changeIndex)) ? [changeIndex] : []
      );
      return changeIndexes.length ? [{ opIndex, changeIndexes }] : [];
    }),
  };
}

export function onlySelectedAdditions(ops: AiProposalOp[], selected: Set<SelectionKey>): boolean {
  return ops.every((op, opIndex) => {
    if (op.op === "items_change") {
      return op.changes.every(
        (change, changeIndex) =>
          !selected.has(changeKey(opIndex, changeIndex)) || change.op === "add"
      );
    }
    return !selected.has(atomicKey(opIndex)) || op.op.startsWith("new_");
  });
}
